import { useRef, useState } from "react";
import { FormGroup } from "@/components/ui/FormLayout";
import { ATTACHMENT_ACCEPT } from "@/lib/uploadAccept";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { SampleFileButton } from "./SampleFileButton";
import { Button } from "@/components/ui/Button";
import { getLocalizedError, uploadFile } from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import { fileIconFor, formatFileSize } from "@/domain/fileMeta";

/**
 * Trần 10 tệp đính kèm.
 *
 * Hệ thống cũ chốt 5 (ATTACHMENT_CONFIG.maxFiles), KHÔNG phải 10 — chú thích cũ
 * ở đây ghi sai và đã lan sang cả zod schema. Giữ 10 vì nới lỏng không gây hại
 * và đơn mẫu thật hay có nhiều ảnh, nhưng ghi đúng mốc cũ để lần sau không ai
 * đối chiếu nhầm.
 */
export const MAX_ATTACHMENTS = 10;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

export interface AttachmentDraft {
  key: string;
  type: "image" | "document";
  fileName: string;
  fileUrl: string;
  fileKey: string;
  fileSize: number;
  mimeType: string;
}

interface SampleOrderAttachmentsProps {
  attachments: AttachmentDraft[];
  onChange: (next: AttachmentDraft[] | ((previous:AttachmentDraft[])=>AttachmentDraft[])) => void;
  readOnly?:boolean;
  /** Raised while an upload is in flight so the page can block submit and warn on navigate. */
  onBusyChange: (busy: boolean) => void;
}

/**
 * Attachment list for a sample order. The parent owns the drafts; this component
 * only uploads to R2 and hands back the complete next array — PUT /sample-orders/:id
 * full-replaces the attachment rows, so a partial array would silently delete the rest.
 */
export function SampleOrderAttachments({ attachments, onChange, onBusyChange, readOnly=false }: SampleOrderAttachmentsProps) {
  const { t } = useTranslation();
  const uploadLock=useRef(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const full = attachments.length >= MAX_ATTACHMENTS;

  const markBusy = (next: boolean) => {
    setBusy(next);
    onBusyChange(next);
  };

  const onFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length || readOnly || uploadLock.current) return;
    uploadLock.current=true;
    markBusy(true);
    setErr(null);
    // Accumulate locally: `attachments` is a prop and cannot update mid-loop.
    let list = attachments;
    try {
      for (const file of files) {
        if (list.length >= MAX_ATTACHMENTS) break;
        // Pre-check locally so the user sees the actual rule rather than the
        // server's image-worded rejection. The server enforces it regardless.
        const isImage = file.type.startsWith("image/");
        const accepted = file.type !== "" && ATTACHMENT_ACCEPT.split(",").includes(file.type);
        // Ảnh: xét dung lượng SAU KHI NÉN. Một tấm chụp bằng điện thoại 8 MB
        // nén xong còn vài trăm KB, chặn nó ở kích thước gốc là từ chối đúng
        // thứ người dùng vừa chụp ở xưởng.
        const sized = isImage ? await compressImage(file) : file;
        if (!accepted || sized.size > (isImage ? MAX_IMAGE_BYTES : MAX_DOCUMENT_BYTES)) {
          setErr(t("samples.attachmentHint"));
          continue;
        }
        const f = await uploadFile(sized, "sample-orders");
        const added = {
          key: f.key, type: f.kind, fileName: f.fileName, fileUrl: f.url,
          fileKey: f.key, fileSize: f.fileSize, mimeType: f.mimeType,
        };
        list = [...list,added];
        // Publish after each file so a mid-batch failure keeps what already landed.
        onChange(previous=>previous.length < MAX_ATTACHMENTS ? [...previous,added] : previous);
      }
    } catch (e2) {
      setErr(getLocalizedError(e2, t, "samples.errorUpload"));
    } finally {
      uploadLock.current=false;
      markBusy(false);
    }
  };

  return (
    // Thẻ nhóm cùng chuẩn biểu mẫu (19/09/2026): tên nhóm, lời dặn, nút thêm tệp ở góc thẻ.
    <FormGroup
      id="sample-group-attachments"
      title={t("samples.attachments")}
      hint={t("samples.attachmentHint")}
      actions={
        <>
          <span className="font-mono tabular text-xs text-ink-faint">
            {t("samples.attachmentCount", { count: attachments.length, max: MAX_ATTACHMENTS })}
          </span>
          <SampleFileButton accept={ATTACHMENT_ACCEPT} multiple disabled={readOnly || full || busy}
            label={t(busy ? "common.uploading" : "samples.addFiles")} onChange={e=>void onFiles(e)}/>
        </>
      }
    >

      {attachments.length === 0 ? (
        <p className="rounded-sm border border-dashed border-line px-4 py-4 text-center text-xs text-ink-faint">{t("samples.noAttachments")}</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {attachments.map((a) => (
            <li key={a.key} className="flex items-center gap-2.5 rounded-sm border border-line bg-surface px-2.5 py-1.5">
              {a.type === "image" && a.fileUrl
                ? <img src={a.fileUrl} alt="" className="size-8 shrink-0 rounded-sm border border-line object-cover" />
                : (() => {
                    // Biểu tượng theo loại: một danh sách 10 tệp cùng một biểu
                    // tượng thì biểu tượng không mang tin gì.
                    const Icon = fileIconFor(a.mimeType);
                    return (
                      <span className="grid size-8 shrink-0 place-items-center rounded-sm border border-line bg-sunken/40">
                        <Icon className="size-4 text-ink-faint" />
                      </span>
                    );
                  })()}
              {a.fileUrl ? (
                <a
                  href={a.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={a.fileName}
                  aria-label={`${t("samples.openAttachment")}: ${a.fileName}`}
                  className="min-w-0 flex-1 truncate text-xs text-ink-soft hover:text-brand hover:underline"
                >
                  {a.fileName}
                </a>
              ) : (
                <span className="min-w-0 flex-1 truncate text-xs text-ink-soft">{a.fileName}</span>
              )}
              <span className="shrink-0 font-mono tabular text-2xs text-ink-faint">
                {formatFileSize(a.fileSize)}
              </span>
              <Button type="button" variant="ghost" size="icon-sm" aria-label={t("samples.deleteAttachment")}
                disabled={busy || readOnly}
                onClick={() => onChange(previous=>previous.filter((x) => x.key !== a.key))}>
                <Trash2 className="size-4 text-ink-faint" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {err && <p className="mt-2 rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger" role="alert">{err}</p>}
    </FormGroup>
  );
}
